import { noOp } from 'common/Constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExpandableTextProps } from './types';

export const useExpandableText = (props: ExpandableTextProps) => {
  const {
    collapseText,
    expandText = 'more',
    numberOfLines,
    onClickExpand = noOp,
    onClickCollapse = noOp,
    text,
    ...otherProps
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [expandTextWidth, setExpandTextWidth] = useState(0);
  const [lineHeight, setLineHeight] = useState(20);

  const expandedRef = useRef<HTMLDivElement>(null);
  const expandTextRef = useRef<HTMLDivElement>(null);
  const singleLineRef = useRef<HTMLDivElement>(null);
  const calculateExpandTextRef = useRef(0);

  const calculateNumberOfLines = Math.max(numberOfLines, 1);
  const calculatedCollapsedHeight = lineHeight * calculateNumberOfLines;
  const collapsedHeight = lineHeight * (calculateNumberOfLines - 1);
  const showExpand = calculatedCollapsedHeight < expandedHeight;

  const setMeasurements = useCallback(() => {
    if (expandedRef.current && singleLineRef.current) {
      setExpandedHeight(expandedRef.current.offsetHeight);
      setLineHeight(singleLineRef.current.offsetHeight);
    }
    if (expandTextRef.current) {
      setExpandTextWidth(expandTextRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    setMeasurements();
  }, [setMeasurements, text]);

  useEffect(() => {
    window.addEventListener('resize', setMeasurements);
    // eslint-disable-next-line functional/immutable-data -- Calculate size
    calculateExpandTextRef.current = window.setInterval(() => {
      if (expandText && expandTextWidth === 0 && expandTextRef.current) {
        setExpandTextWidth(expandTextRef.current.offsetWidth);
      } else {
        clearInterval(calculateExpandTextRef.current);
      }
    }, 400);
    return () => {
      window.removeEventListener('resize', setMeasurements);
      clearInterval(calculateExpandTextRef.current);
    };
  });

  return {
    ...otherProps,
    collapsedHeight,
    collapseText,
    expandedHeight,
    expandedRef,
    expandTextRef,
    expandTextWidth,
    expandText,
    numberOfLines: calculateNumberOfLines,
    handleCollapse: useCallback(() => {
      setIsExpanded(false);
      onClickCollapse();
    }, [onClickCollapse]),
    handleExpand: useCallback(() => {
      setIsExpanded(true);
      onClickExpand();
    }, [onClickExpand]),
    isExpanded,
    lineHeight,
    showExpand,
    singleLineRef,
    text,
  };
};
